import { useCallback, useState } from 'react'

/**
 * A set of ids this browser remembers (John, Nov 5 2026: the holdings rows
 * opened to show their exchange split stay open across a refresh). Read once
 * when the component mounts, written back on every change; storage that is
 * blocked or full only loses the memory, the set still works for the page.
 */
export function usePersistedSet(key: string): [Set<string>, (id: string) => void] {
  const [ids, setIds] = useState<Set<string>>(() => {
    try {
      const raw = window.localStorage.getItem(key)
      const parsed: unknown = raw ? JSON.parse(raw) : []
      return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [])
    } catch {
      return new Set()
    }
  })
  const toggle = useCallback(
    (id: string) => {
      setIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        try {
          window.localStorage.setItem(key, JSON.stringify([...next]))
        } catch {
          // storage blocked: the choice lasts for this page
        }
        return next
      })
    },
    [key],
  )
  return [ids, toggle]
}
